import { z } from 'zod';
import { ROLES, CAR_STATUSES, BG_REMOVAL_MODES, PHOTO_STAGES } from '../constants/index.js';
// --- Auth --------------------------------------------------------------------
export const LoginInput = z.object({
    email: z.string().email().toLowerCase().trim(),
    password: z.string().min(1),
});
export const AcceptInviteInput = z.object({
    token: z.string().min(16),
    password: z.string().min(10, 'Password must be at least 10 characters'),
    name: z.string().min(1).max(120),
});
export const ForgotPasswordInput = z.object({ email: z.string().email().toLowerCase().trim() });
export const ResetPasswordInput = z.object({
    token: z.string().min(16),
    password: z.string().min(10),
});
export const SwitchTenantInput = z.object({ tenantId: z.string().min(1) });
// --- Tenants -----------------------------------------------------------------
export const CreateTenantInput = z.object({
    name: z.string().min(2).max(120),
    slug: z.string().regex(/^[a-z0-9-]+$/).min(2).max(64),
});
export const UpdateTenantInput = z.object({
    name: z.string().min(2).max(120).optional(),
    bgRemovalMode: z.enum(BG_REMOVAL_MODES).optional(),
    removeBgApiKey: z.string().min(1).optional().or(z.literal('')),
    maxCars: z.number().int().positive().max(10_000).optional(),
});
// --- Invites / users ---------------------------------------------------------
export const InviteUserInput = z.object({
    email: z.string().email().toLowerCase().trim(),
    role: z.enum(ROLES),
});
export const UpdateUserInput = z.object({
    name: z.string().min(1).max(120).optional(),
    role: z.enum(ROLES).optional(),
    canSwitchTenants: z.boolean().optional(),
});
// --- Cars --------------------------------------------------------------------
export const CreateCarInput = z.object({
    stockNumber: z.string().max(64).optional(),
    vin: z.string().length(17).regex(/^[A-HJ-NPR-Z0-9]{17}$/).optional(),
    year: z.number().int().min(1900).max(2100).optional(),
    make: z.string().max(64).optional(),
    model: z.string().max(64).optional(),
    trim: z.string().max(64).optional(),
    color: z.string().max(64).optional(),
    notes: z.string().max(2000).optional(),
});
export const UpdateCarInput = CreateCarInput.extend({
    status: z.enum(CAR_STATUSES).optional(),
});
export const ListCarsQuery = z.object({
    q: z.string().optional(),
    status: z.enum(CAR_STATUSES).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
// --- Photos ------------------------------------------------------------------
export const RegisterPhotoInput = z.object({
    slotKey: z.string().min(1).max(64).optional(),
    slotIndex: z.number().int().min(0).max(99),
    stage: z.enum(PHOTO_STAGES).default('ORIGINAL'),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    bytes: z.number().int().positive().optional(),
});
export const CompositeInput = z.object({
    backgroundId: z.string().min(1),
    overlayId: z.string().min(1).optional(),
    overlayTransform: z
        .object({
        x: z.number(),
        y: z.number(),
        scale: z.number().positive().max(10),
        opacity: z.number().min(0).max(1),
        // When true the server stretches the overlay to fill the entire canvas;
        // x/y/scale are ignored. Useful for full-frame banners and tints.
        stretch: z.boolean().optional(),
    })
        .optional(),
    cleanTransform: z
        .object({
        x: z.number(),
        y: z.number(),
        scale: z.number().positive().max(10),
        opacity: z.number().min(0).max(1),
    })
        .optional(),
});
// --- Backgrounds / overlays --------------------------------------------------
export const CreateBackgroundInput = z.object({
    name: z.string().min(1).max(120),
    isDefault: z.boolean().optional(),
});
export const CreateOverlayInput = z.object({
    name: z.string().min(1).max(120),
    defaultX: z.number().int().default(0),
    defaultY: z.number().int().default(0),
    defaultScale: z.number().positive().default(1),
    defaultOpacity: z.number().min(0).max(1).default(1),
});
// --- Photo package -----------------------------------------------------------
export const ShotSlotSchema = z.object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(120),
    instructions: z.string().max(500),
    guide: z.enum(['car-front', 'car-rear', 'car-side', 'car-3q', 'wheel', 'interior', 'detail']),
    order: z.number().int().min(0),
    enabled: z.boolean(),
});
export const UpdatePhotoPackageInput = z.object({ shots: z.array(ShotSlotSchema).min(0).max(50) });
//# sourceMappingURL=index.js.map